/**
 * Mock data for the customer → catalogue + price group assignments journey.
 *
 * Every customer has both a catalogue and a price group, defaulting to Standard;
 * the two move independently (a customer can be on the Core range with
 * custom pricing, or vice versa). The screen is an upsert over the whole roster,
 * with a CSV download/upload (3 columns) for bulk edits. Locally typed for
 * journey iteration; productionises to the owning module's API later.
 */

import type { CustomerAssignment } from "../data/pricing-admin-types";
import { mockCatalogues } from "./catalogue";
import { getMockPriceGroups } from "./pricing";

const roster: CustomerAssignment[] = [
  {
    customerCode: "RAM1379",
    customerName: "Ramsay Healthcare",
    catalogueId: "ramsay",
    priceGroupId: "ramsay",
  },
  // Own catalogue, but shared pricing — proves the two columns are independent.
  {
    customerCode: "PER01",
    customerName: "St James's Place",
    catalogueId: "perivan-sjp",
    priceGroupId: "standard",
  },
  // Shared catalogue, but custom pricing — the other direction.
  {
    customerCode: "JLR22",
    customerName: "Jaguar Land Rover",
    catalogueId: "core-range",
    priceGroupId: "ramsay",
  },
  {
    customerCode: "TES44",
    customerName: "Tesco Stores",
    catalogueId: "core-range",
    priceGroupId: "standard",
  },
  {
    customerCode: "BRAM",
    customerName: "Bramley & Co",
    catalogueId: "core-range",
    priceGroupId: "standard",
  },
  {
    customerCode: "TECH",
    customerName: "TechStart Ltd",
    catalogueId: "core-range",
    priceGroupId: "standard",
  },
  {
    customerCode: "BSCH",
    customerName: "Business School UK",
    catalogueId: "core-range",
    priceGroupId: "standard",
  },
  {
    customerCode: "HEND",
    customerName: "Henderson Group",
    catalogueId: "core-range",
    priceGroupId: "standard",
  },
];

export function getMockAssignments(): CustomerAssignment[] {
  return roster.map((r) => ({ ...r }));
}

export function catalogueName(id: string): string {
  return mockCatalogues.find((c) => c.id === id)?.name ?? id;
}

export function priceGroupName(id: string): string {
  const g = getMockPriceGroups().find((rs) => rs.id === id);
  if (!g) return id;
  return g.scope === "central" ? "Standard" : g.name;
}

/** Options for the catalogue / price group selects. */
export function assignmentOptions(): {
  catalogues: { value: string; label: string }[];
  priceGroups: { value: string; label: string }[];
} {
  return {
    catalogues: mockCatalogues.map((c) => ({ value: c.id, label: c.name })),
    priceGroups: getMockPriceGroups().map((g) => ({
      value: g.id,
      label: g.scope === "central" ? "Standard" : g.name,
    })),
  };
}

// ── CSV (3 columns) ──────────────────────────────────────────────────────────

export const assignmentsCsvFilename = "customer-assignments.csv";

export function assignmentsToCsv(rows: CustomerAssignment[]): string {
  const lines = ["customer,catalogue,price_group"];
  for (const r of rows)
    lines.push(
      `${r.customerName},${catalogueName(r.catalogueId)},${priceGroupName(r.priceGroupId)}`,
    );
  return lines.join("\n") + "\n";
}

/** A single change an uploaded CSV would make against the current roster. */
export interface AssignmentChange {
  customer: string;
  field: "Catalogue" | "Price group";
  from: string;
  to: string;
}

export interface AssignmentUploadReview {
  fileName: string;
  rowsOk: number;
  changes: AssignmentChange[];
  /** CSV rows whose customer didn't match the roster (skipped). */
  unknownCustomers: string[];
}

/** A fixed, illustrative diff returned when a file is "uploaded" in the mock. */
export function getMockAssignmentUpload(): AssignmentUploadReview {
  return {
    fileName: assignmentsCsvFilename,
    rowsOk: 8,
    changes: [
      {
        customer: "Jaguar Land Rover",
        field: "Price group",
        from: "Ramsay",
        to: "Standard",
      },
      {
        customer: "Tesco Stores",
        field: "Catalogue",
        from: "Core range",
        to: "Ramsay Healthcare",
      },
      {
        customer: "Henderson Group",
        field: "Price group",
        from: "Standard",
        to: "Ramsay",
      },
    ],
    unknownCustomers: ["Globex Inc"],
  };
}
